import { useState, useCallback, useMemo, useEffect } from "react";
import { TruckMaterialType } from "../../enums/truckMaterialType";

export const useEstimateTableState = ({
  materialTruckList,
  truckDetails,
  estimateTickedRows,
  setEstimateTickedRows,
  showEstimate,
}) => {
  const dataSource = useMemo(
    () => [
      ...materialTruckList?.map((item, idx) => ({
        ...item,
        _key: `material-${item.id}-${idx}`,
      })),
      ...truckDetails?.map((item, idx) => ({
        ...item,
        ...(item?.materialName === undefined || item?.materialName === null
          ? { materialName: item?.materialTruckName }
          : {}),
        ...(item?.truckingPrice !== null && item?.truckingPrice !== undefined
          ? {
              landedPrice: item?.truckingPrice,
              landedPriceType: item?.truckingPriceType,
            }
          : {}),
        _key: `truck-${item.id}-${idx}`,
      })),
    ]?.filter((item) => item?.isBlocked !== true),
    [materialTruckList, truckDetails]
  );

  const [visiblePrices, setVisiblePrices] = useState<
    Record<string, { material: boolean; trucking: boolean; landed: boolean }>
  >(
    dataSource?.reduce((acc, item) => {
      acc[item?._key] = {
        material: item?.materialPriceType !== item?.truckingPriceType && item?.type !== TruckMaterialType.TRUCK_TYPE,
        trucking: item?.materialPriceType !== item?.truckingPriceType && item?.type !== TruckMaterialType.TRUCK_TYPE,
        landed: item?.type === TruckMaterialType.TRUCK_TYPE || item?.materialPriceType === item?.truckingPriceType,
      };
      return acc;
    }, {})
  );

  const handleRowSelectionChange = useCallback(
    (selectedRowKeys, selectedRows) => {
      const updatedEstimateTickedRows = selectedRows?.map((row) => {
        const key = row?._key;
        const visibility = visiblePrices[key] || {};

        return {
          ...row,
          materialPrice: visibility?.material ? row?.materialPrice : "-",
          truckingPrice: visibility?.trucking ? row?.truckingPrice : "-",
          landedPrice: visibility?.landed ? row?.landedPrice : "-",
        };
      });

      setEstimateTickedRows(updatedEstimateTickedRows);
    },
    [visiblePrices, setEstimateTickedRows]
  );

  const togglePriceVisibility = useCallback(
    (key, type) => {
      setVisiblePrices((prev) => {
        const newVisibility = {
          ...prev,
          [key]: {
            ...prev[key],
            [type]: !prev[key]?.[type],
          },
        };

        setEstimateTickedRows((prevRows) =>
          prevRows?.map((row) => {
            if (row?._key !== key) return row;

            const originalRow = dataSource?.find((r) => r?._key === key);
            if (!originalRow) return row;

            const fieldMap = {
              material: "materialPrice",
              trucking: "truckingPrice",
              landed: "landedPrice",
            };

            const field = fieldMap[type];
            const isVisibleNow = !prev[key]?.[type];

            return {
              ...row,
              [field]:
                isVisibleNow &&
                originalRow[field] !== null &&
                originalRow[field] !== undefined &&
                originalRow[field] !== "-"
                  ? originalRow[field]
                  : "N/A",
            };
          })
        );

        return newVisibility;
      });
    },
    [dataSource, setEstimateTickedRows]
  );

  useEffect(() => {
    const initialVisibility = dataSource?.reduce((acc, item) => {
      acc[item?._key] = {
        material: item?.materialPriceType !== item?.truckingPriceType && item?.type !== TruckMaterialType.TRUCK_TYPE,
        trucking: item?.materialPriceType !== item?.truckingPriceType && item?.type !== TruckMaterialType.TRUCK_TYPE,
        landed: item?.type === TruckMaterialType.TRUCK_TYPE || item?.materialPriceType === item?.truckingPriceType,
      };
      return acc;
    }, {});
    setVisiblePrices(initialVisibility);
  }, [dataSource]);

  return {
    dataSource,
    visiblePrices,
    handleRowSelectionChange,
    togglePriceVisibility,
  };
};
